// @ts-nocheck
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { City, ShippingCalculatorProps } from './types';
import { API_BASE_URL } from './constants';
import { searchCities, getAvailableShippingMethods } from './utils';
import { useTranslations } from './translations'; // Import the translation hook
import CitySelector from './city-selector';
import FormField from './form-field';
import switzerland_flag from './switzerland.png';

/**
 * ShippingCalculator component for handling shipping quotes and requests
 */
const ShippingCalculator: React.FC<ShippingCalculatorProps> = ({ 
  blurBackground = false, 
  transparentBox = false,
  primaryColor = '#00ff08',
  buttonFontColor = "000",
  accentColor = '#0066cc',
  labelColor = '#374151', 
  borderRadius = 'rounded-lg',
  iframeKey = '', // Unique key to identify the iframe source
  badgeActiveColor = '#00ff08', // Default to primary color
  showInternational = true, // Default to showing international tab
  isCarrier = false, // Default to comparison mode
  carrierId = null,
  language, // Language prop
}) => {
  // Get translations with language prop
  const t = useTranslations(language);
  
  // Modal state
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const allowedKey = '0ß23045ß90ß29fsyx24ß91';

  // Tab state - default to national
  const [selectedTab, setSelectedTab] = useState<'national' | 'international'>('national');

  // Form state
  const [pickupInput, setPickupInput] = useState<string>('');
  const [deliveryInput, setDeliveryInput] = useState<string>('');
  const [pickup, setPickup] = useState<City | null>(null);
  const [delivery, setDelivery] = useState<City | null>(null);
  const [customerType, setCustomerType] = useState<string>('');
  const [shippingMethod, setShippingMethod] = useState<string>('');
  const [showShippingMethod, setShowShippingMethod] = useState<boolean>(false);
  const [availableShippingMethods, setAvailableShippingMethods] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Search results state
  const [pickupResults, setPickupResults] = useState<City[]>([]);
  const [deliveryResults, setDeliveryResults] = useState<City[]>([]);
  const [isSearchingPickup, setIsSearchingPickup] = useState<boolean>(false);
  const [isSearchingDelivery, setIsSearchingDelivery] = useState<boolean>(false);

  // Effect to validate locations based on selected tab
  useEffect(() => {
    if (selectedTab === 'national') {
      // For national shipping, both locations must be in Switzerland
      if (pickup && (pickup.countryCode !== 'CH' && pickup.countryCode3 !== 'CHE')) {
        setPickup(null);
        setPickupInput('');
        setErrorMessage(t.nationalOnlySwiss || 'Für nationale Sendungen müssen beide Orte in der Schweiz sein');
      }
      if (delivery && (delivery.countryCode !== 'CH' && delivery.countryCode3 !== 'CHE')) {
        setDelivery(null);
        setDeliveryInput('');
        setErrorMessage(t.nationalOnlySwiss || 'Für nationale Sendungen müssen beide Orte in der Schweiz sein');
      }
    } else if (selectedTab === 'international') {
      // For international shipping, at least one location must be outside Switzerland
      if (pickup && delivery && 
          (pickup.countryCode === 'CH' || pickup.countryCode3 === 'CHE') && 
          (delivery.countryCode === 'CH' || delivery.countryCode3 === 'CHE')) {
        setErrorMessage(t.internationalNeedsAbroad || 'Für internationale Sendungen muss mindestens ein Ort außerhalb der Schweiz sein');
      }
    }
  }, [selectedTab, pickup, delivery, t.nationalOnlySwiss, t.internationalNeedsAbroad]);

  // Update pickup address details
  const handlePickupDetailsChange = useCallback((updatedCity: City) => {
    setPickup(updatedCity);
  }, []);

  // Update delivery address details
  const handleDeliveryDetailsChange = useCallback((updatedCity: City) => {
    setDelivery(updatedCity);
  }, []);

  // Handle clicks outside of dropdowns - removed to prevent conflicts with CitySelector's own handling

  // Update shipping methods when locations or customer type changes
  useEffect(() => {
    // Reset shipping method when locations or customer type changes
    setShippingMethod('');
    
    // Check if both pickup and delivery are selected
    if (pickup && delivery) {
      // For NATIONAL tab - no shipping methods needed
      if (selectedTab === 'national') {
        console.log('National tab selected - No shipping methods needed');
        setShowShippingMethod(false);
        setAvailableShippingMethods([]);
        
        // Validate that both locations are in Switzerland
        if ((pickup.countryCode !== 'CH' && pickup.countryCode3 !== 'CHE') || 
            (delivery.countryCode !== 'CH' && delivery.countryCode3 !== 'CHE')) {
          setErrorMessage(t.nationalOnlySwiss || 'Für nationale Sendungen müssen beide Orte in der Schweiz sein');
        }
      } 
      // For INTERNATIONAL tab - always show shipping methods
      else if (selectedTab === 'international') {
        console.log('International tab selected - Determining available shipping methods');
        
        // Get available shipping methods using the utility function
        const availableMethods = getAvailableShippingMethods(pickup, delivery);
        
        // For international, we always need shipping methods
        if (availableMethods.length > 0) {
          setAvailableShippingMethods(availableMethods);
          // Show shipping method if customer type is selected
          setShowShippingMethod(customerType !== '');
        } else {
          // If no methods available, default to air and sea for international
          console.log('No specific methods found, defaulting to air and sea for international');
          setAvailableShippingMethods(['air', 'sea']);
          setShowShippingMethod(customerType !== '');
        }
        
        // Validate that at least one location is outside Switzerland
        if ((pickup.countryCode === 'CH' || pickup.countryCode3 === 'CHE') && 
            (delivery.countryCode === 'CH' || delivery.countryCode3 === 'CHE')) {
          setErrorMessage(t.internationalNeedsAbroad || 'Für internationale Sendungen muss mindestens ein Ort außerhalb der Schweiz sein');
        }
      }
    } else {
      // If either location is not selected, reset
      setAvailableShippingMethods([]);
      setShowShippingMethod(false);
    }
  }, [pickup, delivery, customerType, selectedTab, t.internationalNeedsAbroad, t.nationalOnlySwiss]);

  // Add debounce for search - separate timeouts for each field
  const pickupDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const deliveryDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (pickupDebounceTimeout.current) {
        clearTimeout(pickupDebounceTimeout.current);
      }
      if (deliveryDebounceTimeout.current) {
        clearTimeout(deliveryDebounceTimeout.current);
      }
    };
  }, []);
  
  // Search for pickup cities with debounce
  const handlePickupSearch = useCallback((value: string) => {
    setPickupInput(value);
    
    // Clear any existing timeout
    if (pickupDebounceTimeout.current) {
      clearTimeout(pickupDebounceTimeout.current);
    }
    
    if (value.length === 0) {
      setPickup(null);
      setPickupResults([]);
      return;
    }
    
    if (value.length < 3) {
      setPickupResults([]);
      return;
    }
    
    // Set new timeout (300ms debounce)
    setIsSearchingPickup(true);
    pickupDebounceTimeout.current = setTimeout(async () => {
      try {
        // Use national endpoint if national tab is selected
        const results = await searchCities(value, selectedTab === 'national');
        // Filter results based on selected tab (additional client-side filtering)
        let filteredResults = results || [];
        if (selectedTab === 'national') {
          // Only show Swiss cities for national shipping
          filteredResults = filteredResults.filter(city => 
            city.countryCode === 'CH' || city.countryCode3 === 'CHE'
          );
        }
        setPickupResults(filteredResults);
      } catch (error) {
        console.error('Error searching cities:', error);
        setPickupResults([]);
      } finally {
        setIsSearchingPickup(false);
      }
    }, 300);
  }, [selectedTab]);

  // Search for delivery cities with debounce
  const handleDeliverySearch = useCallback((value: string) => {
    setDeliveryInput(value);
    
    // Clear any existing timeout
    if (deliveryDebounceTimeout.current) {
      clearTimeout(deliveryDebounceTimeout.current);
    }
    
    if (value.length === 0) {
      setDelivery(null);
      setDeliveryResults([]);
      return;
    }
    
    if (value.length < 3) {
      setDeliveryResults([]);
      return;
    }
    
    // Set new timeout (300ms debounce)
    setIsSearchingDelivery(true);
    deliveryDebounceTimeout.current = setTimeout(async () => {
      try {
        // Use national endpoint if national tab is selected
        const results = await searchCities(value, selectedTab === 'national');
        // Filter results based on selected tab (additional client-side filtering)
        let filteredResults = results || [];
        if (selectedTab === 'national') {
          // Only show Swiss cities for national shipping
          filteredResults = filteredResults.filter(city => 
            city.countryCode === 'CH' || city.countryCode3 === 'CHE'
          );
        } else if (selectedTab === 'international' && pickup) {
          // For international shipping, if pickup is Switzerland, don't show Swiss cities for delivery
          if (pickup.countryCode === 'CH' || pickup.countryCode3 === 'CHE') {
            filteredResults = filteredResults.filter(city => 
              city.countryCode !== 'CH' && city.countryCode3 !== 'CHE'
            );
          }
        }
        setDeliveryResults(filteredResults);
      } catch (error) {
        console.error('Error searching cities:', error);
        setDeliveryResults([]);
      } finally {
        setIsSearchingDelivery(false);
      }
    }, 300);
  }, [selectedTab, pickup]);

  // Handle pickup selection
  const handlePickupSelect = useCallback((value: string, city: City) => {
    setPickupInput(value);
    setPickup(city);
    // Results will be cleared by CitySelector component
  }, []);

  // Handle delivery selection
  const handleDeliverySelect = useCallback((value: string, city: City) => {
    setDeliveryInput(value);
    setDelivery(city);
    // Results will be cleared by CitySelector component
  }, []);

  // Function to render shipping method options based on available methods
  const renderShippingMethodOptions = () => {
    return (
      <>
        <option value="">{t.selection}</option> 
        {availableShippingMethods.includes('land') && (
          <option value="land">{t.landFreight}</option>
        )}
        {availableShippingMethods.includes('air') && (
          <option value="air">{t.airFreight}</option>
        )}
        {availableShippingMethods.includes('sea') && (
          <option value="sea">{t.seaFreight}</option>
        )}
      </>
    );
  };

  // Handle form submission
// Handle form submission - Supporting both Carrier and Main SaaS modes
const handleSubmit = async () => {
    console.log('=== handleSubmit CALLED ===');
    console.log('isCarrier:', isCarrier);
    console.log('carrierId:', carrierId);
    console.log('iframeKey:', iframeKey);
    console.log('pickup:', pickup);
    console.log('delivery:', delivery);
    console.log('customerType:', customerType);
    console.log('shippingMethod:', shippingMethod);
    console.log('selectedTab:', selectedTab);

    // 1️⃣ Reset any previous error messages
    setErrorMessage('');

    // 2️⃣ Mode-specific validation
    if (isCarrier && !carrierId) {
        console.log('VALIDATION FAILED: Carrier mode requires carrierId');
        setErrorMessage('Carrier ID is missing');
        return;
    }

    if (!isCarrier && !iframeKey) {
        console.log('VALIDATION FAILED: Comparison mode requires iframeKey');
        setErrorMessage('Configuration error');
        return;
    }

    // For comparison mode, check if iframe key is allowed
    if (!isCarrier && iframeKey !== allowedKey) {
        console.log('IFRAME KEY MISMATCH - showing modal');
        setShowComingSoonModal(true);
        return;
    }

    // 3️⃣ Validate inputs
    if (!pickup || !delivery) {
        console.log('VALIDATION FAILED: Missing pickup or delivery');
        setErrorMessage(t.pleaseEnterAddresses);
        return;
    }

    // 4️⃣ Validate based on selected tab (National vs International)
    if (selectedTab === 'national') {
        // For national shipping, both must be in Switzerland
        if ((pickup.countryCode !== 'CH' && pickup.countryCode3 !== 'CHE') || 
            (delivery.countryCode !== 'CH' && delivery.countryCode3 !== 'CHE')) {
            console.log('VALIDATION FAILED: National shipping requires both locations in Switzerland');
            setErrorMessage(t.nationalOnlySwiss || 'Für nationale Sendungen müssen beide Orte in der Schweiz sein');
            return;
        }
    } else if (selectedTab === 'international') {
        // For international shipping, at least one must be outside Switzerland
        if ((pickup.countryCode === 'CH' || pickup.countryCode3 === 'CHE') && 
            (delivery.countryCode === 'CH' || delivery.countryCode3 === 'CHE')) {
            console.log('VALIDATION FAILED: International shipping requires at least one location outside Switzerland');
            setErrorMessage(t.internationalNeedsAbroad || 'Für internationale Sendungen muss mindestens ein Ort außerhalb der Schweiz sein');
            return;
        }
    }
    
    // 5️⃣ Validate customer type
    if (!customerType) {
        console.log('VALIDATION FAILED: Missing customerType');
        setErrorMessage(t.pleaseSelectCustomerType);
        return;
    }
    
    // 6️⃣ Validate shipping method (only for international)
    if (showShippingMethod && !shippingMethod) {
        console.log('VALIDATION FAILED: Missing shippingMethod');
        setErrorMessage(t.pleaseSelectShippingMethod);
        return;
    }
    
    console.log('VALIDATION PASSED - Starting API call');
    setIsLoading(true);

    try {
        // 7️⃣ Determine endpoint based on mode
        const endpoint = isCarrier 
            ? '/carrier-requests/iframe/create/'
            : '/shipment/iframe/create-shipment/';
        const fullUrl = `${API_BASE_URL}${endpoint}`;
        console.log('Full URL:', fullUrl);
        console.log('Mode:', isCarrier ? 'CARRIER' : 'COMPARISON');
        
        // 8️⃣ Build payload based on mode
        const payload = {
            // Use appropriate ID based on mode
            ...(isCarrier 
                ? { carrier_id: carrierId } 
                : { iframe_key: iframeKey }
            ),
            
            customer_type: customerType,
            shipment_type: selectedTab === 'national' ? 'domestic' : 'international',
            language: language || 'de',  // Include language in payload
            
            // Pickup address with all fields
            pickup_address: {
                title: pickup.title || `${pickup.postalCode} ${pickup.city}`,
                address_label: pickup.addressLabel || `${pickup.postalCode} ${pickup.city}`,
                lat: pickup.lat,
                lng: pickup.lng,
                street: pickup.street || '',
                city: pickup.city,
                postal_code: pickup.postalCode,
                country: pickup.country,
                company_name: pickup.company_name || '',
                email: pickup.email || '',
                phone: pickup.phone || '',
                additional_info: pickup.additional_info || ''
            },
            
            // Delivery address with all fields
            delivery_address: {
                title: delivery.title || `${delivery.postalCode} ${delivery.city}`,
                address_label: delivery.addressLabel || `${delivery.postalCode} ${delivery.city}`,
                lat: delivery.lat,
                lng: delivery.lng,
                street: delivery.street || '',
                city: delivery.city,
                postal_code: delivery.postalCode,
                country: delivery.country,
                company_name: delivery.company_name || '',
                email: delivery.email || '',
                phone: delivery.phone || '',
                additional_info: delivery.additional_info || ''
            },
            
            // Shipping method for international shipments
            shipping_method: shippingMethod || "land"
        };
        
        console.log('Payload to send:', JSON.stringify(payload, null, 2));
        
        // 9️⃣ Make API request
        console.log('Making fetch request...');
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        console.log('Response received:', response);
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        // 🔟 Handle error responses
        if (!response.ok) {
            const errorData = await response.json();
            console.error('Error response:', errorData);
            throw new Error(errorData.error || errorData.message || 'Error creating shipment');
        }

        // 1️⃣1️⃣ Parse successful response
        const data = await response.json();
        console.log('Success response data:', data);
        
        // 1️⃣2️⃣ Handle redirect based on mode
        if (data.redirectUrl) {
            console.log('Redirect URL found:', data.redirectUrl);
            
            // Ensure the URL has a protocol
            let redirectUrl = data.redirectUrl;
            if (!redirectUrl.startsWith('http://') && !redirectUrl.startsWith('https://')) {
                // Default to https for security
                redirectUrl = 'https://' + redirectUrl;
                console.log('Added protocol to URL:', redirectUrl);
            }
            
            // 1️⃣3️⃣ Check if we're in an iframe and redirect appropriately
            if (window.self !== window.top) {
                console.log('Running in iframe - sending postMessage and redirecting parent');
                
                // Send message to parent window with appropriate data
                window.parent.postMessage({
                    type: isCarrier ? 'CARRIER_REQUEST_CREATED' : 'SHIPMENT_CREATED',
                    ...(isCarrier 
                        ? {
                            requestId: data.requestId,
                            requestNumber: data.requestNumber,
                            carrierId: carrierId
                          }
                        : {
                            shipmentId: data.shipmentId,
                            shipmentNumber: data.shipmentNumber
                          }
                    ),
                    redirectUrl: redirectUrl
                }, '*');
                
                // Redirect parent window
                console.log('Redirecting parent window...');
                window.parent.location.href = redirectUrl;
            } else {
                // Not in iframe - redirect current window
                console.log('Not in iframe - redirecting directly');
                window.location.href = redirectUrl;
            }
        } else {
            console.error('No redirect URL in response');
            setErrorMessage(t.errorOccurred);
        }
    } catch (error) {
        // 1️⃣4️⃣ Handle any errors
        console.error('CATCH BLOCK - Error:', error);
        console.error('Error stack:', error.stack);
        setErrorMessage(error.message || t.errorOccurred);
    } finally {
        // 1️⃣5️⃣ Always stop loading spinner
        console.log('FINALLY block - setting loading to false');
        setIsLoading(false);
    }
};

  // Add function to clear pickup input
  const clearPickupInput = useCallback(() => {
    setPickupInput('');
    setPickup(null);
    setPickupResults([]);
  }, []);

  // Add function to clear delivery input
  const clearDeliveryInput = useCallback(() => {
    setDeliveryInput('');
    setDelivery(null);
    setDeliveryResults([]);
  }, []);

  // Handle tab change
  const handleTabChange = (tab: 'national' | 'international') => {
    setSelectedTab(tab);
    // Clear form when switching tabs
    setPickupInput('');
    setDeliveryInput('');
    setPickup(null);
    setDelivery(null);
    setPickupResults([]);
    setDeliveryResults([]);
    setCustomerType('');
    setShippingMethod('');
    setErrorMessage('');
  };

  // Dynamic background styles based on props
  const backgroundStyle = blurBackground 
    ? 'backdrop-filter backdrop-blur-md bg-white bg-opacity-30' 
    : transparentBox 
      ? 'bg-transparent' 
      : 'bg-white';

  // Button style with customizable primary color
  const buttonStyle = {
    backgroundColor: primaryColor,
    color: buttonFontColor, // Black text for visibility on light green
  };
  
  // Label style with customizable color
  const labelStyle = {
    color: labelColor,
  };
  
  // Common field style class for select input
  // Updated to normalize appearance across devices
  const selectInputClass = "w-full p-2 border border-gray-300 rounded-md bg-white appearance-none";
  
  // Add custom select arrow styles
  const selectContainerClass = "relative";
  const selectArrowStyle = {
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none' as const,
    color: '#6b7280',
  };

  // Tab styles
  const tabBaseStyle = "px-6 py-3 font-medium transition-all duration-200 cursor-pointer flex items-center gap-2 relative";
  const activeTabStyle = blurBackground 
    ? `${tabBaseStyle} backdrop-filter backdrop-blur-md bg-white bg-opacity-30 text-white`
    : `${tabBaseStyle} bg-white text-gray-800`;
  const inactiveTabStyle = blurBackground
    ? `${tabBaseStyle} backdrop-filter backdrop-blur-md bg-white bg-opacity-10 text-white text-opacity-70 hover:bg-opacity-15`
    : `${tabBaseStyle} bg-gray-100 text-gray-600 hover:bg-gray-200`;

  // Modal Component
  const ComingSoonModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-80"
        onClick={() => setShowComingSoonModal(false)}
      />
      
      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl p-8 md:p-10 max-w-lg mx-4 text-center shadow-2xl">
        <h2 className="text-3xl font-bold mb-4" style={{ color: primaryColor }}>
          {t.comingSoon}
        </h2>
        <p className="text-gray-600 text-lg leading-relaxed mb-8">
          {t.comingSoonMessage}
        </p>
        <button
          onClick={() => setShowComingSoonModal(false)}
          className="px-8 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90"
          style={{ backgroundColor: primaryColor }}
        >
          {t.understood}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Add custom CSS for webkit browsers and tab styles */}
      <style>{`
        /* Tab-specific styles */
        .tab-inactive {
          position: relative;
        }
        
        .tab-active {
          position: relative;
        }
        
        /* Normalize select appearance across all devices */
        select {
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          background-image: none !important;
          background-color: white !important;
          border-radius: 0.375rem !important; /* matches rounded-md */
        }
        
        /* Ensure consistent text styling on iOS */
        select {
          font-size: 16px !important; /* Prevents zoom on iOS */
          -webkit-text-size-adjust: 100%;
          -webkit-border-radius: 0.375rem !important;
          padding: 0.5rem !important;
          text-indent: 0.01px;
          text-overflow: '';
        }
        
        /* iOS specific fixes */
        @supports (-webkit-touch-callout: none) {
          select {
            background: white !important;
            border: 1px solid #d1d5db !important;
          }
        }
        
        /* Remove iOS inner shadow */
        select:focus {
          outline: none !important;
          box-shadow: none !important;
          border-color: #3b82f6 !important;
        }
      `}</style>

      {/* Wrapper for calculator with tabs */}
      <div className="relative mt-4">
        {/* Tab Navigation - Only show if international is enabled */}
        {showInternational && (
          <div className="flex gap-1 items-end">
            <button
              onClick={() => handleTabChange('national')}
              className={`${selectedTab === 'national' ? 'tab-active' : 'tab-inactive'} ${selectedTab === 'national' ? activeTabStyle : inactiveTabStyle}`}
              style={selectedTab === 'national' ? { 
                borderTopLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                marginBottom: '-1px',
                paddingBottom: '13px',
                ...(blurBackground ? {} : { 
                  backgroundColor: 'white',
                  boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
                })
              } : {
                borderTopLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                marginBottom: '0'
              }}
            >
              <span>{t.national || 'National'}</span>
              <img src={switzerland_flag} alt="Swiss Flag" className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleTabChange('international')}
              className={`${selectedTab === 'international' ? 'tab-active' : 'tab-inactive'} ${selectedTab === 'international' ? activeTabStyle : inactiveTabStyle}`}
              style={selectedTab === 'international' ? { 
                borderTopLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                marginBottom: '-1px',
                paddingBottom: '13px',
                ...(blurBackground ? {} : { 
                  backgroundColor: 'white',
                  boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
                })
              } : {
                borderTopLeftRadius: '0.5rem',
                borderTopRightRadius: '0.5rem',
                marginBottom: '0'
              }}
            >
              <span>{t.international || 'International'}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 12c0-4.97 4.03-9 9-9s9 4.03 9 9-4.03 9-9 9-9-4.03-9-9z" stroke="currentColor" strokeWidth="2"/>
                <path d="M3.6 9h16.8M3.6 15h16.8" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 3a17.05 17.05 0 00-2.1 9c0 3.33.7 6.4 2.1 9m0-18c1.4 2.6 2.1 5.67 2.1 9s-.7 6.4-2.1 9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
            {/* Fill the remaining space with a border line */}
            <div className="flex-1" style={{ marginBottom: '0' }}></div>
          </div>
        )}

        {/* Main Calculator Container - Connected to tabs */}
        <div 
          className={`${backgroundStyle} ${borderRadius}`} 
          style={{ 
            borderTopLeftRadius: (showInternational && selectedTab === 'national') ? '0' : '0.5rem',
            ...(transparentBox || blurBackground ? {} : { 
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            })
          }}
        >
          {/* Form Content */}
          <div className="p-4">
          {/* PC/Laptop - all inputs in one line */}
          <div className="hidden lg:flex lg:flex-row lg:gap-4 lg:items-end">
            {/* Pickup */}
            <div className="w-1/4">
              <CitySelector
                id="pickup-lg"
                label={t.pickup}
                value={pickupInput}
                onChange={handlePickupSelect}
                selectedCity={pickup}
                isSearching={isSearchingPickup}
                onSearch={handlePickupSearch}
                searchResults={pickupResults}
                clearResults={() => setPickupResults([])}
                labelStyle={labelStyle}
                onAddressDetailsChange={handlePickupDetailsChange}
                onClear={clearPickupInput}
                required
                placeholder={t.enterCityOrZip}
                searchingText={t.searching}
                clearAriaLabel={t.clear}
                isNational={selectedTab === 'national'}
              />
            </div>

            {/* Delivery */}
            <div className="w-1/4">
              <CitySelector
                id="delivery-lg"
                label={t.delivery}
                value={deliveryInput}
                onChange={handleDeliverySelect}
                selectedCity={delivery}
                isSearching={isSearchingDelivery}
                onSearch={handleDeliverySearch}
                searchResults={deliveryResults}
                clearResults={() => setDeliveryResults([])}
                labelStyle={labelStyle}
                onAddressDetailsChange={handleDeliveryDetailsChange}
                onClear={clearDeliveryInput}
                required
                placeholder={t.enterCityOrZip}
                searchingText={t.searching}
                clearAriaLabel={t.clear}
                isNational={selectedTab === 'national'}
              />
            </div>

            {/* Customer Type - Smaller but not too small */}
            <div style={{ width: '15%' }}>
              <FormField
                id="customerType-lg"
                label={t.youAre}
                labelStyle={labelStyle}
                required
              >
                <div className={selectContainerClass}>
                  <select
                    id="customerType-lg"
                    className={selectInputClass}
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                  >
                    <option value="">{t.selection}</option> 
                    <option value="business">{t.company}</option>
                    <option value="person">{t.private}</option>
                  </select>
                  <svg style={selectArrowStyle} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </FormField>
            </div>

            {/* Shipping Method - only shown when needed */}
            {showShippingMethod && availableShippingMethods.length > 0 && (
              <div style={{ width: '10%' }}>
                <FormField
                  id="shippingMethod-lg"
                  label={t.shippingMethod}
                  labelStyle={labelStyle}
                  required
                >
                  <div className={selectContainerClass}>
                    <select
                      id="shippingMethod-lg"
                      className={selectInputClass}
                      value={shippingMethod}
                      onChange={(e) => setShippingMethod(e.target.value)}
                    >
                      {renderShippingMethodOptions()}
                    </select>
                    <svg style={selectArrowStyle} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </FormField>
              </div>
            )}
            
            {/* Submit Button */}
            <div className="flex-1">
              <button
                onClick={handleSubmit}
                className="w-full p-2 font-medium rounded-md focus:outline-none mt-2"
                style={buttonStyle}
                disabled={isLoading}
              >
                {isLoading ? t.processing : (isCarrier ? t.submitRequest : t.compareNow)}
              </button>
              
              {/* Error message */}
              {errorMessage && (
                <div className="mt-2 text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>

          {/* Tablet - stacked layout with two columns */}
          <div className="hidden md:block lg:hidden space-y-4">
            {/* Pickup/Delivery row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Pickup */}
              <CitySelector
                id="pickup-md"
                label={t.pickup}
                value={pickupInput}
                onChange={handlePickupSelect}
                selectedCity={pickup}
                isSearching={isSearchingPickup}
                onSearch={handlePickupSearch}
                searchResults={pickupResults}
                clearResults={() => setPickupResults([])}
                labelStyle={labelStyle}
                onAddressDetailsChange={handlePickupDetailsChange}
                onClear={clearPickupInput}
                required
                placeholder={t.enterCityOrZip}
                searchingText={t.searching}
                clearAriaLabel={t.clear}
                isNational={selectedTab === 'national'}
              />

              {/* Delivery */}
              <CitySelector
                id="delivery-md"
                label={t.delivery}
                value={deliveryInput}
                onChange={handleDeliverySelect}
                selectedCity={delivery}
                isSearching={isSearchingDelivery}
                onSearch={handleDeliverySearch}
                searchResults={deliveryResults}
                clearResults={() => setDeliveryResults([])}
                labelStyle={labelStyle}
                onAddressDetailsChange={handleDeliveryDetailsChange}
                onClear={clearDeliveryInput}
                required
                placeholder={t.enterCityOrZip}
                searchingText={t.searching}
                clearAriaLabel={t.clear}
                isNational={selectedTab === 'national'}
              />
            </div>

            {/* Options row - 2 columns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Customer Type */}
              <FormField
                id="customerType-md"
                label={t.youAre}
                labelStyle={labelStyle}
                required
              >
                <div className={selectContainerClass}>
                  <select
                    id="customerType-md"
                    className={selectInputClass}
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                  >
                    <option value="">{t.selection}</option>
                    <option value="business">{t.company}</option>
                    <option value="person">{t.private}</option>
                  </select>
                  <svg style={selectArrowStyle} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </FormField>

              {/* Shipping Method - Only show if needed */}
              {showShippingMethod && availableShippingMethods.length > 0 ? (
                <FormField
                  id="shippingMethod-md"
                  label={t.shippingMethod}
                  labelStyle={labelStyle}
                  required
                >
                  <div className={selectContainerClass}>
                    <select
                      id="shippingMethod-md"
                      className={selectInputClass}
                      value={shippingMethod}
                      onChange={(e) => setShippingMethod(e.target.value)}
                    >
                      {renderShippingMethodOptions()}
                    </select>
                    <svg style={selectArrowStyle} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </FormField>
              ) : (
                <div></div> // Empty div to maintain grid structure
              )}
            </div>
            

            {/* Submit Button */}
            <div>
              <button
                onClick={handleSubmit}
                className="w-full p-2 font-medium rounded-md focus:outline-none"
                style={buttonStyle}
                disabled={isLoading}
              >
                {isLoading ? t.processing : (isCarrier ? t.submitRequest : t.compareNow)}
              </button>
              
              {/* Error message */}
              {errorMessage && (
                <div className="mt-2 text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>

          {/* Mobile - all fields stacked */}
          <div className="md:hidden space-y-4">
            {/* Pickup */}
            <CitySelector
              id="pickup-sm"
              label={t.pickup}
              value={pickupInput}
              onChange={handlePickupSelect}
              selectedCity={pickup}
              isSearching={isSearchingPickup}
              onSearch={handlePickupSearch}
              searchResults={pickupResults}
              clearResults={() => setPickupResults([])}
              labelStyle={labelStyle}
              onAddressDetailsChange={handlePickupDetailsChange}
              onClear={clearPickupInput}
              required
              placeholder={t.enterCityOrZip}
              searchingText={t.searching}
              clearAriaLabel={t.clear}
              isNational={selectedTab === 'national'}
            />

            {/* Delivery */}
            <CitySelector
              id="delivery-sm"
              label={t.delivery}
              value={deliveryInput}
              onChange={handleDeliverySelect}
              selectedCity={delivery}
              isSearching={isSearchingDelivery}
              onSearch={handleDeliverySearch}
              searchResults={deliveryResults}
              clearResults={() => setDeliveryResults([])}
              labelStyle={labelStyle}
              onAddressDetailsChange={handleDeliveryDetailsChange}
              onClear={clearDeliveryInput}
              required
              placeholder={t.enterCityOrZip}
              searchingText={t.searching}
              clearAriaLabel={t.clear}
              isNational={selectedTab === 'national'}
            />

            {/* Customer Type */}
            <FormField
              id="customerType-sm"
              label={t.youAre}
              labelStyle={labelStyle}
              required
            >
              <div className={selectContainerClass}>
                <select
                  id="customerType-sm"
                  className={selectInputClass}
                  value={customerType}
                  onChange={(e) => setCustomerType(e.target.value)}
                >
                  <option value="">{t.selection}</option>
                  <option value="business">{t.company}</option>
                  <option value="person">{t.private}</option>
                </select>
                <svg style={selectArrowStyle} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </FormField>

            {/* Shipping Method - conditional */}
            {showShippingMethod && availableShippingMethods.length > 0 && (
              <FormField
                id="shippingMethod-sm"
                label={t.shippingMethod}
                labelStyle={labelStyle}
                required
              >
                <div className={selectContainerClass}>
                  <select
                    id="shippingMethod-sm"
                    className={selectInputClass}
                    value={shippingMethod}
                    onChange={(e) => setShippingMethod(e.target.value)}
                  >
                    {renderShippingMethodOptions()}
                  </select>
                  <svg style={selectArrowStyle} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </FormField>
            )}

            {/* Button */}
            <div>
              <button
                onClick={handleSubmit}
                className="w-full p-2 font-medium rounded-md focus:outline-none"
                style={buttonStyle}
                disabled={isLoading}
              >
                {isLoading ? t.processing : t.compareNow}
              </button>
              
              {/* Error message */}
              {errorMessage && (
                <div className="mt-2 text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Coming Soon Modal */}
      {showComingSoonModal && <ComingSoonModal />}
    </>
  );
};

export default ShippingCalculator;